import React, { useEffect, useRef, useState } from "react";
import copy from "copy-to-clipboard";
import { markdownToJSX } from "../../utils/markdown-util";
import CopyIcon from "../../assets/copy-icon.svg";
import CopiedIcon from "../../assets/copied-icon.svg";
import LikeIcon from "../../assets/like-icon.svg";
import LikedIcon from "../../assets/liked-icon.svg";
import DisLikedIcon from "../../assets/disliked-icon.svg";
import DisLikeIcon from "../../assets/dislike-icon.svg";
import "./styles.css";
import { getExtendedApiWrapper } from "../../services/api-handlers";

const ChatBubble = ({
  messages = [],
  initialFeedback = [],
  username,
  programName,
}) => {
  const [feedbackStatus, setFeedbackStatus] = useState({});
  const [copied, setCopied] = useState(false);
  const messagesEndRef = useRef(null);
  const api = getExtendedApiWrapper();


  useEffect(() => {
    const initialFeedbackMap = {};
    initialFeedback.forEach((feedback, index) => {
      initialFeedbackMap[messages[index].message_id] = feedback;
    });
    setFeedbackStatus(initialFeedbackMap);
  }, [initialFeedback, messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFeedback = async (message_id, feedback) => {
    try {
      // Send feedback as 0 or 1 to the API (like or dislike)
      const apiFeedback = feedback.liked ? 0 : feedback.disliked ? 1 : null; // 0 for like, 1 for dislike

      // If feedback is null, it means removing the feedback, so send no feedback (null or undefined)
      await api.sendFeedback(
        message_id,
        apiFeedback,
        programName
      );

      // Update the feedback state after successful API call
      setFeedbackStatus((prevStatus) => ({
        ...prevStatus,
        [message_id]: feedback,
      }));
    } catch (error) {
      console.error("Error sending feedback:", error);
    }
  };

  const handleLikeClick = (msg) => {
    const currentFeedback = feedbackStatus[msg.message_id];
    if (currentFeedback?.liked) {
      // If already liked, remove the like feedback (set liked to false)
      handleFeedback(msg.message_id, {
        liked: false,
        disliked: currentFeedback?.disliked,
      });
    } else {
      // If dislike was previously selected, remove it and set like feedback
      handleFeedback(msg.message_id, { liked: true, disliked: false });
    }
  };

  const handleDislikeClick = (msg) => {
    const currentFeedback = feedbackStatus[msg.message_id];
    if (currentFeedback?.disliked) {
      // If already disliked, remove the dislike feedback (set disliked to false)
      handleFeedback(msg.message_id, {
        liked: currentFeedback?.liked,
        disliked: false,
      });
    } else {
      // If like was previously selected, remove it and set dislike feedback
      handleFeedback(msg.message_id, { liked: false, disliked: true });
    }
  };

  const handleCopied = () => {
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const handleCopy = (message) => {
    copy(message);
    handleCopied();
  };

  return (
    <div className="chat-container">
      {Array.isArray(messages) &&
        messages.map((msg, index) => (
          <div key={msg.message_id || index} className="messages">
            <div
              className={`msg-source ${
                msg.user_type === "bot" ? "msg-source-bot" : "msg-source-user"
              }`}
            >
              {msg.user_type === "bot" ? `AI Coach` : `${username}`}
            </div>
            <div className={`message ${msg.user_type}`}>
              <p>{markdownToJSX(msg.message)}</p>
              {msg.user_type === "bot" && (
                <div className="icon-container">
                  {copied ? (
                    <img loading="lazy" src={CopiedIcon} alt="copy" />
                  ) : (
                    <button
                      className="img-button"
                      onClick={() => handleCopy(msg.message)}
                    >
                      <img loading="lazy" src={CopyIcon} alt="copy" />
                    </button>
                  )}
                  <button
                    className="img-button"
                    onClick={() => handleLikeClick(msg)}
                  >
                    <img
                      loading="lazy"
                      src={
                        feedbackStatus[msg.message_id]?.liked
                          ? LikedIcon
                          : LikeIcon
                      }
                      alt="like"
                    />
                  </button>

                  <button
                    className="img-button"
                    onClick={() => handleDislikeClick(msg)}
                  >
                    <img
                      loading="lazy"
                      src={
                        feedbackStatus[msg.message_id]?.disliked
                          ? DisLikedIcon
                          : DisLikeIcon
                      }
                      alt="dislike"
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatBubble;
